import React, { BaseSyntheticEvent, useEffect, useMemo, useState } from 'react';
import dynamic from 'next/dynamic';
import cn from 'classnames';
import Image from 'next/image';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper';
import { Navigation } from 'swiper/modules';
import { useTranslation } from 'next-i18next';
import {
  MecImage,
  isPrimaryProductImage,
} from '@adapters/bigcommerce/types/product';
import { normalizeImageVariantPreference } from '@utils/imageVariantPreference';
import IconWrapper from '@components/ui/Icons';
import { usePdp } from '@context/PdpContext';
import PromoBadge from '../PromoBadge';
import BuyCanadian from '@components/ui/BuyCanadian';
import { FEATURE_FLAG_SHOP_CANADIAN } from '@config/feature-flag';

import 'swiper/css';

import styles from './ProductSlider.module.scss';

const ProductSlideZoomModal = dynamic(() => import('./ProductSlideZoomModal'));

export interface ProductSliderSwiperProps {
  imageGallery: MecImage[];
  madeInCanada?: boolean;
  isCanadianBrand?: boolean;
}

const ProductSliderSwiper = ({
  imageGallery,
  madeInCanada = false,
  isCanadianBrand = false,
}: ProductSliderSwiperProps) => {
  const { selectedColour, promoBadge, setSelectedColourId, colours } = usePdp();
  const orderedImageGallery = useMemo(
    () =>
      normalizeImageVariantPreference(imageGallery, {
        preferTransparent: false,
      }),
    [imageGallery]
  );
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperType | null>(null);
  const [mainSwiper, setMainSwiper] = useState<SwiperType | null>(null);

  const [activeThumb, setActiveThumb] = useState(0);

  const [fadeLeft, setFadeLeft] = useState(false);
  const [fadeRight, setFadeRight] = useState(false);

  const [zoom, setZoom] = useState({
    isVisible: false,
    slideNum: 0,
  });

  const [displayOverlay, setDisplayOverlay] = useState(false);
  const showMapleLeaf = FEATURE_FLAG_SHOP_CANADIAN;

  const { t } = useTranslation(['pdp', 'common']);

  const zoomClickHandler = (slideNum = 0) => {
    setZoom((state) => ({ isVisible: !state.isVisible, slideNum }));
  };

  const mainSlides = orderedImageGallery.map((media, i) => (
    <SwiperSlide key={media.urlOriginal} onClick={() => zoomClickHandler(i)}>
      <div
        className={cn(styles.mainImage, {
          [styles.mainImageOverlay]: displayOverlay,
        })}
      >
        <Image
          src={media.urlOriginal}
          alt={media.altText}
          priority={i === 0}
          quality={65}
          fill
          sizes="(min-width: 1190px) 800px,(min-width: 720px) calc(100vw - 21.25rem), 100vw"
          style={{
            objectFit: 'contain',
            objectPosition: 'center',
          }}
        />
        {displayOverlay && (
          <div className={styles.mainImageOverlayText}>
            {t('pdp:product.imageComingSoon')}
          </div>
        )}
      </div>
    </SwiperSlide>
  ));

  const advanceThumbSlide = (
    el: HTMLElement | undefined,
    slider: SwiperType | null
  ): void => {
    if (el && !el?.classList?.contains('swiper-slide-visible')) {
      slider?.slideTo(slider?.slides?.indexOf(el));
    } else if (
      el?.nextElementSibling &&
      !el.nextElementSibling.classList?.contains('swiper-slide-visible')
    ) {
      slider?.slideNext();
    } else if (
      el?.previousElementSibling &&
      !el.previousElementSibling.classList?.contains('swiper-slide-visible')
    ) {
      slider?.slidePrev();
    }
  };

  useEffect(() => {
    if (selectedColour?.name) {
      const slideNum = orderedImageGallery?.findIndex((img) => {
        if ('urlOriginal' in img) {
          return (
            isPrimaryProductImage(img) &&
            img.swatchMatch === selectedColour.name
          );
        } else {
          return false;
        }
      });
      if (slideNum !== -1) {
        setDisplayOverlay(false);
        mainSwiper?.slideTo(slideNum, 0);
        advanceThumbSlide(thumbsSwiper?.slides[slideNum], thumbsSwiper);
      }
      if (slideNum === -1) {
        setDisplayOverlay(true);
        mainSwiper?.slideTo(0, 0);
        advanceThumbSlide(thumbsSwiper?.slides[0], thumbsSwiper);
      }
    }
  }, [mainSwiper, orderedImageGallery, selectedColour, thumbsSwiper]);

  const getColorFromMedia = (media: MecImage) => {
    if (!('urlOriginal' in media)) return null;
    return colours?.find((colour) => colour.name === media.swatchMatch);
  };

  const thumbs = orderedImageGallery.map((media, i) => {
    const getColour = getColorFromMedia(media);

    return (
      <SwiperSlide
        key={media.urlOriginal}
        className={cn(styles.thumbSlide, {
          [styles.thumbSlideActive]: activeThumb === i,
          [styles.thumbSlideOverlay]: displayOverlay && activeThumb === i,
        })}
        onClick={(e: BaseSyntheticEvent) => {
          mainSwiper?.slideTo(i);
          const el = e.target.closest('.swiper-slide');
          advanceThumbSlide(el, thumbsSwiper);
          setDisplayOverlay(false);
          if (getColour?.id) {
            setSelectedColourId(getColour.id);
          }
        }}
      >
        <Image
          src={media.urlOriginal}
          alt={media.altText}
          width={100}
          height={100}
          quality={55}
          priority={i === 0}
          sizes="(min-width: 1088px) 110px, (min-width: 720px) 98px, (min-width: 480px) 67px, 47px"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            objectPosition: 'center',
          }}
        />
        {displayOverlay && activeThumb === i && (
          <div className={cn(styles.thumbSlideOverlayText)}>
            {t('pdp:product.imageComingSoon')}
          </div>
        )}
      </SwiperSlide>
    );
  });

  const setFade = (swiper: SwiperType) => {
    setFadeLeft(!swiper.isBeginning);
    setFadeRight(!swiper.isEnd);
  };

  return (
    <>
      <style type="text/css">
        {`
          .swiper:not(.swiper-initialized) .swiper-slide:nth-child(${1})
          { order: -1; }
        `}
      </style>

      <div className="mec-slider swiper-parent">
        <Swiper
          modules={[Navigation]}
          id="main"
          className={styles.swiper}
          navigation={{
            nextEl: styles.swipeButtNext,
            prevEl: styles.swipeButtPrev,
          }}
          spaceBetween={0}
          slidesPerView={1}
          initialSlide={0}
          onSwiper={setMainSwiper}
          threshold={2}
          onInit={() => {
            setActiveThumb(0);
          }}
          onSlideChange={(swiper) => {
            setActiveThumb(swiper.activeIndex);
            advanceThumbSlide(
              thumbsSwiper?.slides[swiper.activeIndex],
              thumbsSwiper
            );
          }}
        >
          {mainSlides}
          {showMapleLeaf && (madeInCanada || isCanadianBrand) && (
            <div className={styles.buyCanadianWrapper}>
              {madeInCanada && <BuyCanadian isMadeInCanada={madeInCanada} />}
              {isCanadianBrand && !madeInCanada && (
                <BuyCanadian isCanadianBrand={isCanadianBrand} />
              )}
            </div>
          )}
          <button
            type="button"
            className={cn(styles.swipeButtPrev, {
              [styles.swipeButtHide]: mainSwiper?.isBeginning,
            })}
            onClick={() => {
              mainSwiper?.slidePrev();
              const getActiveThumb =
                orderedImageGallery[mainSwiper?.activeIndex ?? 0];
              const getColour = getColorFromMedia(getActiveThumb);
              if (getColour?.id) {
                setSelectedColourId(getColour.id);
              }
            }}
          >
            <span className={styles.screenReaderOnly}>
              {t('common:carousel.prev')}
            </span>
            <IconWrapper icon="caret-left" width={30} height={30} />
          </button>
          <button
            type="button"
            className={cn(styles.swipeButtNext, {
              [styles.swipeButtHide]: mainSwiper?.isEnd,
            })}
            onClick={() => {
              mainSwiper?.slideNext();
              const getActiveThumb =
                orderedImageGallery[mainSwiper?.activeIndex ?? 0];
              const getColour = getColorFromMedia(getActiveThumb);
              if (getColour?.id) {
                setSelectedColourId(getColour.id);
              }
            }}
          >
            <span className={styles.screenReaderOnly}>
              {t('common:carousel.next')}
            </span>
            <IconWrapper icon="caret-right" width={30} height={30} />
          </button>
        </Swiper>
        <div className={styles.thumbWrapper}>
          <PromoBadge promotion={promoBadge} placement="pdp" />
          <Swiper
            id="thumbs"
            className={styles.swiper}
            slidesPerView={6}
            spaceBetween={8}
            threshold={2}
            watchSlidesProgress
            onSwiper={setThumbsSwiper}
            initialSlide={0}
            onInit={setFade}
            onSlideChange={setFade}
            onReachBeginning={setFade}
            onReachEnd={setFade}
            breakpoints={{
              1061: {
                slidesPerView: 7,
              },
              901: {
                slidesPerView: 6,
              },
              720: {
                spaceBetween: 16,
              },
              704: {
                slidesPerView: 5,
              },
              480: {
                slidesPerView: 10,
              },
              400: {
                slidesPerView: 7,
                spaceBetween: 8,
              },
            }}
          >
            {thumbs}
          </Swiper>
          <div
            role="button"
            aria-hidden
            className={cn(styles.fadeLeft, {
              [styles.fadeLeftHide]: !fadeLeft,
            })}
          />
          <div
            role="button"
            aria-hidden
            className={cn(styles.fadeRight, {
              [styles.fadeRightHide]: !fadeRight,
            })}
          />
        </div>

        {zoom.isVisible && (
          <ProductSlideZoomModal
            imageGallery={orderedImageGallery}
            startingSlide={zoom.slideNum}
            handleClose={zoomClickHandler}
          />
        )}
      </div>
    </>
  );
};

export default ProductSliderSwiper;
